/**
 * TemplateList — every disclosure template, searchable and filterable.
 *
 * 2026-10-07 rewrite (Bill's 2026-10-06 capture of /disclosures/templates):
 *   - "Active version" showed "undefined" on 52 of 54 rows: it read `.version`,
 *     a field the catalog never sends (the column is versionNumber).
 *   - "Status" and "Active" sat side by side with the same word and different
 *     meanings. Now: "Live version" (the version packages use, by the server's rule
 *     — status Active, not closed) and "Switched on" (the template flag).
 *   - Pending work (drafts, pending approval, approved-not-active) is shown per row.
 *   - Filters: search, how it is added, product type, who signs, live status, on/off.
 *   - "Delete" was a soft delete that only switched the template off; it is now
 *     labelled "Switch off" and confirms in plain words.
 *
 * Endpoints: GET /v3/admin/disclosures/catalog + /options (via DisclosuresContext),
 * POST /v3/admin/disclosures/templates (new), DELETE /v3/admin/disclosures/templates/{code}.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, Plus, RefreshCw, Search, PowerOff } from '@retail-ops/panel-shell';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import type { DisclosureTemplateRow } from '@/services/disclosuresApi';
import { deleteTemplate } from '@/services/disclosuresAdminApi';
import { ConfirmDialog, PillStatus, Select, TextInput, Toast, useToast } from './_shared';
import { TemplateEditorModal } from './TemplateEditorModal';
import {
  itemFor,
  liveVersion,
  pendingVersions,
  requiresSignature,
  splitCsv,
  statusLabel,
  versionsFor,
} from './disclosureModel';

interface Filters {
  q: string;
  layer: string;
  category: string;
  signature: 'any' | 'yes' | 'no';
  live: 'any' | 'live' | 'none' | 'pending';
  onOff: 'any' | 'on' | 'off';
}

const DEFAULT_FILTERS: Filters = { q: '', layer: '', category: '', signature: 'any', live: 'any', onOff: 'any' };

export function TemplateList(): JSX.Element {
  const navigate = useNavigate();
  const { envelope, state, error, refresh, options, lastLoadedAt } = useDisclosuresConfig();
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [confirmOff, setConfirmOff] = useState<DisclosureTemplateRow | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const { toast, push, clear } = useToast();

  const enriched = useMemo(
    () =>
      envelope.templates.map((t) => {
        const code = String(t.code ?? '');
        const vs = versionsFor(envelope.versions, code);
        return { t, code, vs, live: liveVersion(vs), pending: pendingVersions(vs) };
      }),
    [envelope.templates, envelope.versions],
  );

  const rows = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return enriched.filter(({ t, code, live, pending }) => {
      if (q && !code.toLowerCase().includes(q) && !String(t.displayName ?? '').toLowerCase().includes(q)) return false;
      if (filters.layer && String(t.layer ?? '') !== filters.layer) return false;
      if (filters.category) {
        const cats = splitCsv(t.productCategory).map((c) => c.toUpperCase());
        const all = cats.length === 0 || cats.includes('ALL');
        if (filters.category === 'ALL' ? !all : !cats.includes(filters.category.toUpperCase())) return false;
      }
      if (filters.signature === 'yes' && !requiresSignature(t)) return false;
      if (filters.signature === 'no' && requiresSignature(t)) return false;
      if (filters.live === 'live' && !live) return false;
      if (filters.live === 'none' && live) return false;
      if (filters.live === 'pending' && pending.length === 0) return false;
      if (filters.onOff === 'on' && t.isActive === false) return false;
      if (filters.onOff === 'off' && t.isActive !== false) return false;
      return true;
    });
  }, [enriched, filters]);

  const onSwitchOff = useCallback(async (): Promise<void> => {
    if (!confirmOff) return;
    const code = String(confirmOff.code ?? '');
    setBusy(true);
    try {
      const result = await deleteTemplate(code);
      if (!result.success) {
        push('err', result.error ?? 'Could not switch it off.');
        return;
      }
      push('ok', `${code} switched off. It is left out of the next package built.`);
      setConfirmOff(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  }, [confirmOff, push, refresh]);

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setFilters((f) => ({ ...f, [k]: v }));
  const filtered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  return (
    <>
      <Toast toast={toast} onClose={clear} />

      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/" className="back-link">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/disclosures" className="back-link">Disclosure Engine</Link>
        <span aria-hidden="true"> / </span>
        <span>Templates</span>
      </nav>

      <Link to="/disclosures" className="back-link">
        <ArrowLeft size={14} /> Back to Disclosure Engine
      </Link>

      <section className="page-header">
        <h1 className="page-header__title">Disclosure templates</h1>
        <p className="page-header__subtitle page-intro">
          Every document the package builder can include. Open one to see who gets it,
          who signs it, and its versions. Only a template&apos;s <strong>active</strong> version
          is ever used in a live package; drafts and versions pending approval are not.
        </p>
      </section>

      <header className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Search size={14} aria-hidden="true" />
          <TextInput
            placeholder="Search code or name…"
            value={filters.q}
            onChange={(e) => set('q', e.target.value)}
            style={{ minWidth: 200, width: 220 }}
            aria-label="Search code or name"
          />
        </div>
        <Select value={filters.layer} onChange={(e) => set('layer', e.target.value)} aria-label="How it is added" title="How it is added" style={{ width: 190 }}>
          <option value="">Any way of adding</option>
          {(options?.layers ?? []).map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </Select>
        <Select value={filters.category} onChange={(e) => set('category', e.target.value)} aria-label="Product type" title="Product type" style={{ width: 170 }}>
          <option value="">Any product type</option>
          <option value="ALL">Every product type</option>
          {(options?.productCategories ?? []).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>
        <Select value={filters.signature} onChange={(e) => set('signature', e.target.value as Filters['signature'])} aria-label="Signature" title="Signature" style={{ width: 170 }}>
          <option value="any">Signed or not</option>
          <option value="yes">Needs a signature</option>
          <option value="no">No signature</option>
        </Select>
        <Select value={filters.live} onChange={(e) => set('live', e.target.value as Filters['live'])} aria-label="Live version" title="Live version" style={{ width: 190 }}>
          <option value="any">Any version state</option>
          <option value="live">Has an active version</option>
          <option value="none">No active version</option>
          <option value="pending">Has changes in progress</option>
        </Select>
        <Select value={filters.onOff} onChange={(e) => set('onOff', e.target.value as Filters['onOff'])} aria-label="Switched on" title="Switched on" style={{ width: 140 }}>
          <option value="any">On or off</option>
          <option value="on">Switched on</option>
          <option value="off">Switched off</option>
        </Select>
        {filtered && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setFilters(DEFAULT_FILTERS)}>
            Clear filters
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void refresh()} disabled={state === 'loading'}>
          <RefreshCw size={14} /> {state === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={() => setCreateOpen(true)} disabled={!options}>
          <Plus size={14} /> New template
        </button>
      </header>

      {error && <div className="banner banner--err" role="alert">{error}</div>}
      {state === 'loading' && rows.length === 0 && <div className="empty">Loading templates…</div>}
      {state === 'ok' && rows.length === 0 && <div className="empty">No templates match these filters.</div>}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--striped table--hover">
            <thead>
              <tr>
                <th>Template</th>
                <th>How it is added</th>
                <th>Product types</th>
                <th>Who signs</th>
                <th>Live version</th>
                <th>In progress</th>
                <th>On</th>
                <th className="col-actions" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ t, code, live, pending }) => {
                const open = () => navigate(`/disclosures/templates/${encodeURIComponent(code)}`);
                const cats = splitCsv(t.productCategory).filter((c) => c.toUpperCase() !== 'ALL');
                const sig = itemFor(options?.signatureRequirements, t.signatureRequirement);
                return (
                  <tr key={code} onClick={open} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{String(t.displayName ?? code)}</div>
                      <code className="cell-id" style={{ fontSize: 11 }}>{code}</code>
                    </td>
                    <td>{itemFor(options?.layers, t.layer)?.label ?? String(t.layer ?? '—')}</td>
                    <td>{['universal', 'product', 'ownership'].includes(String(t.layer)) ? (cats.length ? cats.join(', ') : 'Every type') : '—'}</td>
                    <td title={sig?.meaning ?? String(t.signatureRequirement ?? '')}>
                      {sig?.label ?? (t.signatureRequirement ? `${String(t.signatureRequirement)} (unknown)` : '—')}
                    </td>
                    <td>
                      {live ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <span>{String(live.versionNumber)}</span>
                          <PillStatus status="Active" />
                        </span>
                      ) : (
                        <span className="pill pill--err">None</span>
                      )}
                    </td>
                    <td>
                      {pending.length === 0
                        ? '—'
                        : pending.map((v) => (
                            <span key={v.versionId} className="pill pill--warn" style={{ marginRight: 4, fontSize: 11 }} title={`Version ${String(v.versionNumber)}`}>
                              {String(v.versionNumber)}: {statusLabel(v.status)}
                            </span>
                          ))}
                    </td>
                    <td>
                      {t.isActive === false ? <span className="pill pill--muted">Off</span> : <span className="pill pill--ok">On</span>}
                    </td>
                    <td className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                      {t.isActive !== false && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          title="Switch off"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setConfirmOff(t);
                          }}
                          style={{ color: 'var(--pcb-red)' }}
                        >
                          <PowerOff size={12} /> Switch off
                        </button>
                      )}{' '}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          open();
                        }}
                      >
                        Open <ArrowRight size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <footer className="list-footer">
        <span>
          Showing {rows.length} of {envelope.templates.length}
          {lastLoadedAt ? ` · loaded ${lastLoadedAt.toLocaleTimeString()}` : ''}
        </span>
      </footer>

      <TemplateEditorModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSaved={(code) => {
          setCreateOpen(false);
          push('ok', `Template ${code} created. Add a version next.`);
          void refresh();
          navigate(`/disclosures/templates/${encodeURIComponent(code)}`);
        }}
        onError={(msg) => push('err', msg)}
      />

      <ConfirmDialog
        open={confirmOff !== null}
        title="Switch this template off?"
        confirmLabel="Switch off"
        message={
          <span>
            <strong>{String(confirmOff?.displayName ?? '')}</strong> (<code>{String(confirmOff?.code ?? '')}</code>) will be
            left out of packages built from now on, except where a customer-type rule requires it
            (the template page lists those). Its versions and history stay on file, and you can
            switch it back on from its Edit dialog.
          </span>
        }
        busy={busy}
        onConfirm={() => void onSwitchOff()}
        onCancel={() => setConfirmOff(null)}
      />
    </>
  );
}
